import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '../components/ui/resizable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Badge } from '../components/ui/badge';
import api from '../lib/api';
import { getAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Loader2, Save, Edit, X, Plus, RefreshCw, ZoomIn, ZoomOut, Check, AlertTriangle, AlertCircle, History, Trash2, Layers, FileStack, CheckCircle, Download, ExternalLink, Lock, UserCheck, ChevronDown, Copy } from 'lucide-react';
import { format } from 'date-fns';

// Use CDN worker that matches the bundled pdfjs version
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export const ConsolidatedViewer = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const { user } = getAuth();
  const currentAccountStr = localStorage.getItem('current_account');
  const currentAccount = currentAccountStr ? JSON.parse(currentAccountStr) : null;
  
  const [document, setDocument] = useState(null);
  const [loading, setLoading] = useState(true);
  const [numPages, setNumPages] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  
  // PDF Zoom
  const [scale, setScale] = useState(1.0);
  const [currentPage, setCurrentPage] = useState(1);
  const pdfContainerRef = useRef(null);
  
  // Active tab
  const [activeTab, setActiveTab] = useState('statement');
  
  // BillPortal export
  const [hasBillPortalExport, setHasBillPortalExport] = useState(false);
  
  // Document workflow
  const [reviewStatus, setReviewStatus] = useState('review');
  const [assignedTo, setAssignedTo] = useState(null);
  const [accountUsers, setAccountUsers] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAuditModal, setShowAuditModal] = useState(false);
  
  // Editing row tracking - to highlight which row is being edited
  const [editingRowId, setEditingRowId] = useState(null); // Format: 'invoices-0', 'meters-2', 'usage-5'
  const [reviewedRows, setReviewedRows] = useState(new Set()); // Rows marked as worked/checked
  const [showValidationWarnings, setShowValidationWarnings] = useState(false);
  
  // Data state - Unified schema for both single and consolidated bills
  const [statement, setStatement] = useState({
    // Property/Location Info (for consolidated: billed address)
    property_name: '',
    billed_address: '',
    city: '',
    state: '',
    postcode: '',
    // Customer Info
    billed_customer_name: '',
    parent_account_no: '',
    // Vendor Info
    vendor_name: '',
    commodity: '',
    // Invoice Info
    invoice_no: '',
    issue_date: '',
    due_date: '',
    period_from: '',
    period_to: '',
    // Financial Info
    total_charges: '',
    previous_balance: '',
    late_fee: '',
    total_amount_due: ''
  });
  const [childInvoices, setChildInvoices] = useState([]);
  const [meterReads, setMeterReads] = useState([]);
  const [usageDetails, setUsageDetails] = useState([]);

  // Total validation calculation
  const totalValidation = useMemo(() => {
    const parseAmount = (val) => {
      if (!val || val === '' || val === 'None') return 0;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      return parseFloat(cleaned) || 0;
    };

    // Calculate sum of child invoice (new_charges + balance_brought_forward)
    const sumNewCharges = childInvoices.reduce((sum, inv) => {
      return sum + parseAmount(inv.new_charges);
    }, 0);
    
    const sumBalanceBroughtForward = childInvoices.reduce((sum, inv) => {
      return sum + parseAmount(inv.balance_brought_forward);
    }, 0);
    
    const sumChildTotal = sumNewCharges + sumBalanceBroughtForward;

    // Get statement total (try total_amount_due first, then total_charges)
    const statementTotalAmountDue = parseAmount(statement.total_amount_due);
    const statementTotalCharges = parseAmount(statement.total_charges);
    const statementTotal = statementTotalAmountDue > 0 ? statementTotalAmountDue : statementTotalCharges;
    
    // Calculate variance
    const variance = Math.abs(statementTotal - sumChildTotal);
    const variancePercent = statementTotal > 0 ? (variance / statementTotal) * 100 : 0;
    
    // Threshold: 1% or $1 tolerance
    const isValid = variance <= 1 || variancePercent <= 1;
    
    return {
      sumNewCharges: sumNewCharges.toFixed(2),
      sumBalanceBroughtForward: sumBalanceBroughtForward.toFixed(2),
      sumChildTotal: sumChildTotal.toFixed(2),
      statementTotal: statementTotal.toFixed(2),
      variance: variance.toFixed(2),
      variancePercent: variancePercent.toFixed(1),
      isValid,
      hasData: childInvoices.length > 0 && statementTotal > 0
    };
  }, [childInvoices, statement.total_amount_due, statement.total_charges]);

  // Filter usage to show only usage items (exclude taxes, fees, etc.)
  const filteredUsageDetails = useMemo(() => {
    // Keywords that indicate non-usage line items to exclude
    const excludeKeywords = [
      'tax', 'sales tax', 'state tax', 'local tax', 'fee', 'surcharge', 
      'customer charge', 'service charge', 'minimum charge', 'late fee',
      'admin', 'regulatory', 'franchise', 'gross receipts', 'adjustment'
    ];
    
    // Filter out non-usage items
    const usageOnly = usageDetails.filter(item => {
      const desc = (item.line_item_description || '').toLowerCase();
      const hasExcluded = excludeKeywords.some(keyword => desc.includes(keyword));
      // Keep items that have usage quantity != 0 (including negative for credits) and are not excluded
      const hasUsage = parseFloat(item.usage_quantity || 0) !== 0;
      return !hasExcluded && hasUsage;
    });
    
    // For Gas: If both CCF and Therms exist for same account/meter, prefer Therms
    const accountMeterMap = new Map();
    usageOnly.forEach(item => {
      const key = `${item.child_account_no || ''}-${item.meter_number || ''}`;
      const uom = (item.uom || '').toLowerCase();
      if (!accountMeterMap.has(key)) {
        accountMeterMap.set(key, { ccf: [], therms: [], other: [] });
      }
      if (uom === 'ccf') {
        accountMeterMap.get(key).ccf.push(item);
      } else if (uom === 'therms' || uom === 'therm') {
        accountMeterMap.get(key).therms.push(item);
      } else {
        accountMeterMap.get(key).other.push(item);
      }
    });
    
    // Build final list: prefer therms over CCF for gas
    const finalItems = [];
    accountMeterMap.forEach((items) => {
      // If both CCF and Therms exist, only include Therms
      if (items.therms.length > 0 && items.ccf.length > 0) {
        finalItems.push(...items.therms);
      } else {
        // Otherwise include both
        finalItems.push(...items.ccf, ...items.therms);
      }
      // Always include non-gas items
      finalItems.push(...items.other);
    });
    
    return finalItems;
  }, [usageDetails]);
  
  // Calculate unique meter count for Meter tab
  const uniqueMeterCount = useMemo(() => {
    const uniqueMeters = new Set();
    meterReads.forEach(meter => {
      const meterKey = meter.meter_number || meter.child_account_no || '';
      if (meterKey) uniqueMeters.add(meterKey);
    });
    return uniqueMeters.size;
  }, [meterReads]);
  
  // Calculate unique account/meter count for Usage tab
  const uniqueUsageCount = useMemo(() => {
    const uniqueKeys = new Set();
    filteredUsageDetails.forEach(usage => {
      const key = `${usage.child_account_no || ''}-${usage.meter_number || ''}`;
      uniqueKeys.add(key);
    });
    return uniqueKeys.size;
  }, [filteredUsageDetails]);

  // Date validation helper - validates date fields for child invoices
  const validateInvoiceDates = useCallback((invoice) => {
    const validations = {
      due_date: { isValid: true, message: '' },
      period_from: { isValid: true, message: '' },
      period_to: { isValid: true, message: '' },
      period_range: { isValid: true, message: '' }
    };
    
    const parseDate = (dateStr) => {
      if (!dateStr || dateStr === '' || dateStr === 'None') return null;
      const d = new Date(dateStr);
      return isNaN(d.getTime()) ? null : d;
    };
    
    const dueDate = parseDate(invoice.due_date);
    const periodFrom = parseDate(invoice.period_from);
    const periodTo = parseDate(invoice.period_to);
    const today = new Date();
    
    // Due date validation
    if (!dueDate) {
      validations.due_date = { isValid: false, message: 'Due date missing' };
    } else if (dueDate < new Date(today.getFullYear() - 2, 0, 1)) {
      validations.due_date = { isValid: false, message: 'Due date seems too old' };
    }
    
    // Period From validation
    if (!periodFrom) {
      validations.period_from = { isValid: false, message: 'Period start missing' };
    }
    
    // Period To validation
    if (!periodTo) {
      validations.period_to = { isValid: false, message: 'Period end missing' };
    }
    
    // Period range validation (period_to should be after period_from)
    if (periodFrom && periodTo && periodTo < periodFrom) {
      validations.period_range = { isValid: false, message: 'Period end before start' };
    }
    
    // Period should be reasonable (not more than 90 days typically)
    if (periodFrom && periodTo) {
      const daysDiff = (periodTo - periodFrom) / (1000 * 60 * 60 * 24);
      if (daysDiff > 90) {
        validations.period_range = { isValid: false, message: `Period spans ${Math.round(daysDiff)} days` };
      }
    }
    
    return validations;
  }, []);

  // Calculate date validations for all child invoices
  const invoiceDateValidations = useMemo(() => {
    return childInvoices.map(invoice => validateInvoiceDates(invoice));
  }, [childInvoices, validateInvoiceDates]);

  // Meter read date validation
  const meterValidation = useMemo(() => {
    const validations = meterReads.map((read, index) => {
      const prevDate = read.previous_read_date;
      const currDate = read.current_read_date;
      
      if (!prevDate || !currDate) {
        return { index, isValid: true, message: '' }; // Skip if dates missing
      }
      
      const prevDateObj = new Date(prevDate);
      const currDateObj = new Date(currDate);
      
      if (isNaN(prevDateObj.getTime()) || isNaN(currDateObj.getTime())) {
        return { index, isValid: true, message: '' }; // Skip invalid dates
      }
      
      if (prevDateObj >= currDateObj) {
        return {
          index,
          isValid: false,
          message: `Meter ${read.meter_number || index + 1}: Previous read date (${prevDate}) must be before current read date (${currDate})`
        };
      }
      
      return { index, isValid: true, message: '' };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    return { validations, hasErrors, errorMessages };
  }, [meterReads]);

  // Usage line item validation (usage × rate = charge)
  const usageValidation = useMemo(() => {
    const parseNum = (val) => {
      if (!val || val === '' || val === 'None' || val === '-') return null;
      const cleaned = String(val).replace(/[$,]/g, '').trim();
      const num = parseFloat(cleaned);
      return isNaN(num) ? null : num;
    };

    const validations = filteredUsageDetails.map((item, index) => {
      const usage = parseNum(item.usage_quantity);
      const rate = parseNum(item.rate);
      const charge = parseNum(item.charge);
      
      // Skip validation if any required field is missing
      if (usage === null || rate === null || charge === null) {
        return { index, isValid: true, message: '', calculated: null };
      }
      
      const calculated = usage * rate;
      const difference = Math.abs(calculated - charge);
      const tolerance = Math.max(0.02, Math.abs(charge * 0.01)); // 1% or $0.02 tolerance
      
      if (difference > tolerance) {
        return {
          index,
          isValid: false,
          message: `Row ${index + 1}: ${usage} × ${rate} = ${calculated.toFixed(2)}, but charge shows ${charge.toFixed(2)}`,
          calculated
        };
      }
      
      return { index, isValid: true, message: '', calculated };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    return { validations, hasErrors, errorMessages };
  }, [filteredUsageDetails]);

  // UOM validation based on commodity pattern
  const uomValidation = useMemo(() => {
    const expectedUOM = {
      'Electricity': ['kWh', 'kwh', 'KWH', 'MWh', 'mwh'],
      'Gas': ['Therms', 'therms', 'THERMS', 'CCF', 'ccf', 'MCF', 'Dth', 'DTH'],
      'Water': ['Gallons', 'gallons', 'GAL', 'gal', 'HCF', 'hcf', 'CCF', 'ccf', 'kGal', 'KGAL'],
      'Sewer': ['Gallons', 'gallons', 'GAL', 'gal', 'HCF', 'hcf', 'CCF', 'ccf']
    };

    // Count UOM patterns by commodity to validate against majority
    const commodityUomCounts = {};
    filteredUsageDetails.forEach(item => {
      const commodity = item.commodity || statement.commodity;
      const uom = item.uom;
      if (commodity && uom) {
        if (!commodityUomCounts[commodity]) commodityUomCounts[commodity] = {};
        commodityUomCounts[commodity][uom] = (commodityUomCounts[commodity][uom] || 0) + 1;
      }
    });

    const validations = filteredUsageDetails.map((item, index) => {
      const commodity = item.commodity || statement.commodity;
      const uom = item.uom;
      
      if (!commodity || !uom) {
        return { index, isValid: true, message: '' };
      }
      
      const expected = expectedUOM[commodity] || [];
      if (expected.length === 0) {
        return { index, isValid: true, message: '' };
      }
      
      const isValidUOM = expected.some(e => uom.toLowerCase().includes(e.toLowerCase()));
      
      if (!isValidUOM) {
        return {
          index,
          isValid: false,
          message: `Row ${index + 1}: UOM "${uom}" unexpected for ${commodity}. Expected: ${expected.slice(0, 3).join(', ')}`
        };
      }
      
      return { index, isValid: true, message: '' };
    });
    
    const hasErrors = validations.some(v => !v.isValid);
    const errorMessages = validations.filter(v => !v.isValid).map(v => v.message);
    
    // Calculate pattern validation summary
    const patternSummary = Object.entries(commodityUomCounts).map(([commodity, uoms]) => {
      const total = Object.values(uoms).reduce((a, b) => a + b, 0);
      const mostCommon = Object.entries(uoms).sort((a, b) => b[1] - a[1])[0];
      return { commodity, mostCommonUOM: mostCommon?.[0], count: mostCommon?.[1], total };
    });
    
    return { validations, hasErrors, errorMessages, patternSummary };
  }, [filteredUsageDetails, statement.commodity]);

  useEffect(() => {
    if (!currentAccount) {
      toast.error('No account selected');
      navigate('/bill-jobs');
      return;
    }
    fetchDocument();
    fetchPdfAsBlob();
    fetchAccountUsers();
    fetchAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, currentAccount?.id, navigate]);

  const fetchAccountUsers = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/users-list`);
      setAccountUsers(data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchAuditLogs = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}/audit`);
      setAuditLogs(data);
    } catch (error) {
      console.error('Failed to load audit logs');
    }
  };

  const fetchPdfAsBlob = async () => {
    if (!currentAccount) return;
    try {
      // First get file info (type and URL)
      const response = await api.get(
        `/accounts/${currentAccount.id}/documents/${documentId}/file`
      );
      
      let blob;
      if (response.data.type === 's3') {
        // Fetch from S3 presigned URL directly
        const s3Response = await fetch(response.data.url);
        blob = await s3Response.blob();
      } else {
        // Local file - use the download endpoint
        const blobResponse = await api.get(
          `/accounts/${currentAccount.id}/documents/${documentId}/file/download`,
          { responseType: 'blob' }
        );
        blob = new Blob([blobResponse.data], { type: 'application/pdf' });
      }
      
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error('Failed to load PDF:', error);
      toast.error('Failed to load PDF file');
    }
  };

  const fetchDocument = async () => {
    if (!currentAccount) return;
    try {
      const { data } = await api.get(`/accounts/${currentAccount.id}/documents/${documentId}`);
      setDocument(data.document);
      
      // Set document workflow state
      setReviewStatus(data.document.review_status || 'review');
      setAssignedTo(data.document.assigned_to || null);
      
      // Check if BillPortal export exists
      setHasBillPortalExport(data.document.billportal_exported || false);
      
      if (data.extracted_data) {
        // Set consolidated bill data
        setStatement(data.extracted_data.statement || {});
        setChildInvoices(data.extracted_data.child_invoices || []);
        setMeterReads(data.extracted_data.meter_reads || []);
        setUsageDetails(data.extracted_data.usage_details || []);
      }
    } catch (error) {
      console.error('Failed to load document:', error);
      toast.error('Failed to load document');
      navigate('/bill-jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updatedData = {
        bill_type: 'consolidated',
        statement,
        child_invoices: childInvoices,
        meter_reads: meterReads,
        usage_details: usageDetails,
        child_count: childInvoices.length
      };
      
      await api.put(`/accounts/${currentAccount.id}/documents/${documentId}/extracted-data`, {
        data: updatedData
      });
      
      toast.success('Changes saved successfully');
      setEditMode(false);
      fetchDocument();
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleReparse = async (mode = 'all') => {
    setReparsing(true);
    try {
      await api.post(`/accounts/${currentAccount.id}/documents/${documentId}/reprocess`, {
        mode: mode  // 'all' or 'missing'
      });
      toast.success(mode === 'missing' ? 'Re-parsing missing fields. Please wait...' : 'Full re-parsing started. Please wait...');
      setTimeout(() => {
        fetchDocument();
        setReparsing(false);
      }, 5000);
    } catch (error) {
      toast.error('Failed to start re-parsing');
      setReparsing(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/status`, {
        review_status: newStatus
      });
      setReviewStatus(newStatus);
      toast.success(`Status changed to ${newStatus}`);
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleAssignment = async (userId) => {
    try {
      await api.patch(`/accounts/${currentAccount.id}/documents/${documentId}/assign`, {
        user_id: userId || null
      });
      const assignedUser = accountUsers.find(u => u.id === userId);
      setAssignedTo(userId);
      toast.success(userId ? `Assigned to ${assignedUser?.name}` : 'Assignment removed');
      fetchDocument();
      fetchAuditLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign');
    }
  };

  // Memoize PDF.js options to avoid unnecessary reloads
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`,
  }), []);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
  };

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.1, 0.5));
  const handleZoomReset = () => setScale(1.0);
  
  // Navigate to specific page in PDF
  const navigateToPage = useCallback((pageNum) => {
    if (!pageNum || !numPages) return;
    const page = parseInt(pageNum, 10);
    if (page < 1 || page > numPages) return;
    
    setCurrentPage(page);
    
    // Scroll to the page element
    if (pdfContainerRef.current) {
      const pageElement = pdfContainerRef.current.querySelector(`[data-page-number="${page}"]`);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    
    toast.info(`Navigating to page ${page}`, { duration: 1500 });
  }, [numPages]);

  // Handle row focus - highlight row and navigate to page (text highlighting disabled)
  const handleRowFocus = useCallback((rowId, pageNumber) => {
    setEditingRowId(rowId);
    if (pageNumber) {
      navigateToPage(pageNumber);
    }
  }, [navigateToPage]);

  // Mark row as reviewed/worked
  const toggleRowReviewed = useCallback((rowId) => {
    setReviewedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  // Download JSON handler
  const handleDownloadJSON = async () => {
    if (!currentAccount || !documentId) return;
    try {
      // First check if export exists, if not create it
      if (!hasBillPortalExport) {
        toast.info('Converting to BillPortal format...');
        await api.post(`/accounts/${currentAccount.id}/parser-exports/${documentId}/convert`);
        setHasBillPortalExport(true);
      }
      
      // Fetch the parser export
      const { data } = await api.get(`/accounts/${currentAccount.id}/parser-exports`);
      const exportData = data.exports?.find(e => e.document_id === documentId);
      
      if (exportData && exportData.data) {
        // Create and download JSON file
        const jsonStr = JSON.stringify(exportData.data, null, 2);
        const blob = new Blob([jsonStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const link = window.document.createElement('a');
        link.href = url;
        link.download = `${document?.filename?.replace('.pdf', '') || 'bill'}_billportal.json`;
        link.click();
        URL.revokeObjectURL(url);
        toast.success('JSON downloaded');
      } else {
        toast.error('Export data not found');
      }
    } catch (error) {
      toast.error('Failed to download JSON');
      console.error(error);
    }
  };

  // Child invoice handlers
  const addChildInvoice = () => {
    setChildInvoices([...childInvoices, {
      customer_name: '',
      unit_address: '',
      child_account_no: '',
      child_invoice_no: '',
      new_charges: '0',
      total_amount_due: '0',
      balance_brought_forward: '0',
      late_fee: '0',
      sales_tax: '0',
      issue_date: '',
      period_from: '',
      period_to: '',
      due_date: '',
      review_status: 'review'
    }]);
  };

  const updateChildInvoice = (index, field, value) => {
    const updated = [...childInvoices];
    updated[index] = { ...updated[index], [field]: value };
    setChildInvoices(updated);
  };

  const removeChildInvoice = (index) => {
    if (window.confirm('Are you sure you want to remove this child invoice?')) {
      setChildInvoices(childInvoices.filter((_, i) => i !== index));
    }
  };

  // Paste-All functionality for Summary Tab
  const pasteAllField = (field, sourceIndex = 0) => {
    if (childInvoices.length === 0) return;
    const sourceValue = childInvoices[sourceIndex]?.[field] || '';
    if (!sourceValue) {
      toast.error('No value in first row to copy');
      return;
    }
    setChildInvoices(childInvoices.map(inv => ({ ...inv, [field]: sourceValue })));
    toast.success(`Copied "${field.replace(/_/g, ' ')}" to all rows`);
  };

  // Paste-All functionality for Meter Tab
  const pasteAllMeterField = (field, sourceIndex = 0) => {
    if (meterReads.length === 0) return;
    const sourceValue = meterReads[sourceIndex]?.[field] || '';
    if (!sourceValue) {
      toast.error('No value in first row to copy');
      return;
    }
    setMeterReads(meterReads.map(meter => ({ ...meter, [field]: sourceValue })));
    toast.success(`Copied "${field.replace(/_/g, ' ')}" to all rows`);
  };

  // Meter read handlers
  const addMeterRead = () => {
    setMeterReads([...meterReads, {
      child_account_no: '',
      meter_number: '',
      previous_read_date: '',
      current_read_date: '',
      previous_read: '',
      current_read: '',
      read_type: 'Actual'
    }]);
  };

  const updateMeterRead = (index, field, value) => {
    const updated = [...meterReads];
    updated[index] = { ...updated[index], [field]: value };
    setMeterReads(updated);
  };

  const removeMeterRead = (index) => {
    if (window.confirm('Remove this meter reading?')) {
      setMeterReads(meterReads.filter((_, i) => i !== index));
    }
  };

  // Usage detail handlers
  const addUsageDetail = () => {
    setUsageDetails([...usageDetails, {
      child_account_no: '',
      meter_number: '',
      commodity: 'Electricity',
      usage_type: 'Supply',
      line_item_description: '',
      usage_quantity: '0',
      uom: 'kWh',
      rate: '0',
      line_item_charge: '0'
    }]);
  };

  const updateUsageDetail = (index, field, value) => {
    const updated = [...usageDetails];
    updated[index] = { ...updated[index], [field]: value };
    setUsageDetails(updated);
  };

  const removeUsageDetail = (index) => {
    if (window.confirm('Remove this usage item?')) {
      setUsageDetails(usageDetails.filter((_, i) => i !== index));
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/bill-jobs')}
            className="rounded-none"
            data-testid="back-to-bills"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Intake Hub
          </Button>
          
          <div className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-purple-600" />
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-bold tracking-tight">{document?.filename}</h1>
                {hasBillPortalExport && (
                  <Badge variant="outline" className="rounded-none text-[10px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200">
                    BP
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="rounded-none text-xs bg-purple-50 text-purple-700 border-purple-200">
                  Consolidated Bill
                </Badge>
                <Badge variant="secondary" className="rounded-none text-xs">
                  <FileStack className="h-3 w-3 mr-1" />
                  {childInvoices.length} Bills / Accounts
                </Badge>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Download PDF */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (pdfUrl) {
                      const link = window.document.createElement('a');
                      link.href = pdfUrl;
                      link.download = document?.filename || 'bill.pdf';
                      link.click();
                    }
                  }}
                  disabled={!pdfUrl}
                  className="rounded-none"
                  data-testid="download-bill"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Download PDF</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          {/* Download JSON */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadJSON}
                  disabled={document?.status !== 'completed'}
                  className="rounded-none"
                  data-testid="download-json"
                >
                  <FileText className="h-4 w-4" />
                  <span className="ml-1 text-xs">JSON</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">{hasBillPortalExport ? 'Download BillPortal JSON' : 'Convert & Download JSON'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={reparsing || document?.status === 'processing'}
                className="rounded-none"
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${reparsing || document?.status === 'processing' ? 'animate-spin' : ''}`} />
                {reparsing ? 'Re-parsing...' : document?.status === 'processing' ? 'Processing...' : 'Re-parse'}
                <ChevronDown className="h-4 w-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="rounded-none">
              <DropdownMenuItem onClick={() => handleReparse('all')} className="cursor-pointer">
                <RefreshCw className="h-4 w-4 mr-2" />
                Re-parse All Fields
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleReparse('missing')} className="cursor-pointer">
                <AlertCircle className="h-4 w-4 mr-2" />
                Fill Missing Fields Only
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          
          {/* Audit Trail Button */}
          <Button
            variant="outline"
            size="sm"
            className="rounded-none"
            onClick={() => { fetchAuditLogs(); setShowAuditModal(true); }}
          >
            <History className="h-4 w-4 mr-2" />
            History
          </Button>
          
          {editMode ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setEditMode(false);
                  fetchDocument();
                }}
                className="rounded-none"
              >
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="rounded-none font-bold"
              >
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </>
          ) : (
            <Button
              size="sm"
              onClick={() => setEditMode(true)}
              disabled={reviewStatus === 'approved'}
              className="rounded-none font-bold"
              title={reviewStatus === 'approved' ? 'Document is approved and locked for editing' : ''}
            >
              {reviewStatus === 'approved' ? (
                <Lock className="h-4 w-4 mr-2" />
              ) : (
                <Edit className="h-4 w-4 mr-2" />
              )}
              {reviewStatus === 'approved' ? 'Locked' : 'Edit'}
            </Button>
          )}
        </div>
      </div>
      
      {/* Workflow Bar */}
      <div className="border-b border-border bg-card px-6 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase font-medium">Status:</Label>
            <Select value={reviewStatus} onValueChange={handleStatusChange}>
              <SelectTrigger className="w-32 h-8 rounded-none text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="review">Review</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2">
            <Label className="text-xs uppercase font-medium">Assigned:</Label>
            <Select value={assignedTo || '__unassigned__'} onValueChange={(value) => handleAssignment(value === '__unassigned__' ? null : value)}>
              <SelectTrigger className="w-40 h-8 rounded-none text-xs">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__unassigned__">Unassigned</SelectItem>
                {accountUsers.map((u) => (
                  <SelectItem key={u.id} value={u.id}>
                    {u.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {reviewStatus === 'approved' && (
          <Badge className="rounded-none bg-blue-600">
            <Lock className="h-3 w-3 mr-1" />
            Approved & Locked
          </Badge>
        )}
      </div>
      
      {/* Main Content */}
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* PDF Viewer */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="h-full flex flex-col bg-muted/30">
            {/* PDF Controls */}
            <div className="border-b border-border bg-card px-4 py-2 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleZoomOut} className="rounded-none h-8 w-8 p-0">
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm font-mono w-16 text-center">{Math.round(scale * 100)}%</span>
                <Button variant="outline" size="sm" onClick={handleZoomIn} className="rounded-none h-8 w-8 p-0">
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="sm" onClick={handleZoomReset} className="rounded-none text-xs">
                  Reset
                </Button>
              </div>
              {numPages && (
                <span className="text-sm text-muted-foreground">
                  Page {currentPage} of {numPages}
                </span>
              )}
            </div>
            
            {/* PDF Document */}
            <div className="flex-1 overflow-auto p-4" ref={pdfContainerRef}>
              {pdfUrl ? (
                <Document
                  file={pdfUrl}
                  onLoadSuccess={onDocumentLoadSuccess}
                  options={pdfOptions}
                  loading={<Loader2 className="h-8 w-8 animate-spin mx-auto" />}
                  className="flex flex-col items-center gap-4"
                >
                  {Array.from(new Array(numPages), (el, index) => (
                    <Page
                      key={`page_${index + 1}`}
                      pageNumber={index + 1}
                      scale={scale}
                      className={`shadow-lg transition-all ${currentPage === index + 1 ? 'ring-4 ring-blue-500 ring-offset-2' : ''}`}
                      renderTextLayer={true}
                      renderAnnotationLayer={false}
                      data-page-number={index + 1}
                      onRenderError={(error) => console.warn('Page render error:', error)}
                    />
                  ))}
                </Document>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              )}
            </div>
          </div>
        </ResizablePanel>
        
        <ResizableHandle withHandle />
        
        {/* Data Panel */}
        <ResizablePanel defaultSize={50} minSize={30}>
          <div className="h-full flex flex-col overflow-hidden">
            {/* Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              <div className="border-b border-border bg-card px-4">
                <TabsList className="rounded-none h-10 p-0 bg-transparent">
                  <TabsTrigger value="statement" className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-4">
                    Statement
                  </TabsTrigger>
                  <TabsTrigger value="summary" className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-4">
                    Summary ({childInvoices.length})
                  </TabsTrigger>
                  <TabsTrigger value="meter" className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-4">
                    Meter ({uniqueMeterCount})
                  </TabsTrigger>
                  <TabsTrigger value="usage" className="rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm px-4">
                    Usage ({uniqueUsageCount})
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <div className="flex-1 overflow-auto">
                {/* Statement Tab - Unified layout matching Single Bill viewer */}
                <TabsContent value="statement" className="m-0 p-6 h-full overflow-auto">
                  <h3 className="text-lg font-bold mb-4">Bill Statement</h3>
                  
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {/* Row 1: Property/Customer Info */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Property Name</Label>
                      <Input
                        value={statement.property_name || ''}
                        onChange={(e) => setStatement({ ...statement, property_name: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Vendor Name</Label>
                      <Input
                        value={statement.vendor_name || ''}
                        onChange={(e) => setStatement({ ...statement, vendor_name: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Commodity</Label>
                      <Select
                        value={statement.commodity || 'Electricity'}
                        onValueChange={(value) => setStatement({ ...statement, commodity: value })}
                        disabled={!editMode}
                      >
                        <SelectTrigger className="rounded-none mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Electricity">Electricity</SelectItem>
                          <SelectItem value="Water">Water</SelectItem>
                          <SelectItem value="Gas">Gas</SelectItem>
                          <SelectItem value="Sewer">Sewer</SelectItem>
                          <SelectItem value="Waste">Waste</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Row 2: Account Info */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Parent Account No</Label>
                      <Input
                        value={statement.parent_account_no || ''}
                        onChange={(e) => setStatement({ ...statement, parent_account_no: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Billed Customer Name</Label>
                      <Input
                        value={statement.billed_customer_name || ''}
                        onChange={(e) => setStatement({ ...statement, billed_customer_name: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    
                    {/* Row 3: Address */}
                    <div className="col-span-2">
                      <Label className="text-xs uppercase tracking-wide font-medium">Billed Address</Label>
                      <Input
                        value={statement.billed_address || ''}
                        onChange={(e) => setStatement({ ...statement, billed_address: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">City</Label>
                      <Input
                        value={statement.city || ''}
                        onChange={(e) => setStatement({ ...statement, city: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    
                    {/* Row 4: State/Zip/Invoice */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">State</Label>
                      <Input
                        value={statement.state || ''}
                        onChange={(e) => setStatement({ ...statement, state: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Postcode</Label>
                      <Input
                        value={statement.postcode || ''}
                        onChange={(e) => setStatement({ ...statement, postcode: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Invoice No</Label>
                      <Input
                        value={statement.invoice_no || ''}
                        onChange={(e) => setStatement({ ...statement, invoice_no: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    
                    {/* Row 5: Dates */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Issue Date</Label>
                      <Input
                        type="date"
                        value={statement.issue_date || ''}
                        onChange={(e) => setStatement({ ...statement, issue_date: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Period From</Label>
                      <Input
                        type="date"
                        value={statement.period_from || ''}
                        onChange={(e) => setStatement({ ...statement, period_from: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Period To</Label>
                      <Input
                        type="date"
                        value={statement.period_to || ''}
                        onChange={(e) => setStatement({ ...statement, period_to: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    
                    {/* Row 6: Due Date & Financial */}
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Due Date</Label>
                      <Input
                        type="date"
                        value={statement.due_date || ''}
                        onChange={(e) => setStatement({ ...statement, due_date: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Previous Balance</Label>
                      <Input
                        value={statement.previous_balance || '0'}
                        onChange={(e) => setStatement({ ...statement, previous_balance: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs uppercase tracking-wide font-medium">Late Fee</Label>
                      <Input
                        value={statement.late_fee || '0'}
                        onChange={(e) => setStatement({ ...statement, late_fee: e.target.value })}
                        disabled={!editMode}
                        className="rounded-none mt-1 font-mono text-sm"
                      />
                    </div>
                  </div>
                  
                  {/* Financial Summary with Validation */}
                  <div className="border-t border-border pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-wide font-medium flex items-center gap-2">
                          Total Charges
                          {totalValidation.hasData && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="inline-flex items-center">
                                    {totalValidation.isValid ? (
                                      <CheckCircle className="h-4 w-4 text-green-500" />
                                    ) : (
                                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                                    )}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  {totalValidation.isValid ? (
                                    <p className="text-xs">
                                      <span className="font-semibold text-green-600">Validated</span><br/>
                                      Child Total: ${totalValidation.sumChildTotal}<br/>
                                      Statement Total: ${totalValidation.statementTotal}
                                    </p>
                                  ) : (
                                    <p className="text-xs">
                                      <span className="font-semibold text-amber-600">Variance: ${totalValidation.variance}</span><br/>
                                      Child Total: ${totalValidation.sumChildTotal}<br/>
                                      Statement Total: ${totalValidation.statementTotal}
                                    </p>
                                  )}
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </Label>
                        <Input
                          value={statement.total_charges || '0'}
                          onChange={(e) => setStatement({ ...statement, total_charges: e.target.value })}
                          disabled={!editMode}
                          className={`rounded-none mt-1 font-mono text-sm ${totalValidation.hasData && !totalValidation.isValid ? 'border-amber-400' : ''}`}
                        />
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-wide font-medium">Total Amount Due</Label>
                        <Input
                          value={statement.total_amount_due || '0'}
                          onChange={(e) => setStatement({ ...statement, total_amount_due: e.target.value })}
                          disabled={!editMode}
                          className="rounded-none mt-1 font-mono text-sm"
                        />
                      </div>
                    </div>
                    
                    {/* Validation Summary Card */}
                    {totalValidation.hasData && (
                      <div className={`mt-4 p-3 border rounded-none ${totalValidation.isValid ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
                        <div className="flex items-center gap-2">
                          {totalValidation.isValid ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <AlertTriangle className="h-4 w-4 text-amber-600" />
                          )}
                          <span className={`text-sm ${totalValidation.isValid ? 'text-green-700' : 'text-amber-700'}`}>
                            {totalValidation.isValid 
                              ? 'Total Check: Statement total matches child invoice sum' 
                              : `Variance: Statement (${totalValidation.statementTotal}) vs Child Sum (${totalValidation.sumChildTotal})`}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                {/* Summary Tab - Child Invoices */}
                <TabsContent value="summary" className="m-0 h-full overflow-hidden">
                  <div className="h-full flex flex-col p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Child Invoice Summary</h3>
                      {editMode && (
                        <Button size="sm" onClick={addChildInvoice} className="rounded-none">
                          <Plus className="h-4 w-4 mr-1" /> Add Invoice
                        </Button>
                      )}
                    </div>
                    
                    {childInvoices.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No child invoices found
                      </div>
                    ) : (
                      <div className="flex-1 overflow-auto border border-border">
                        <table className="text-sm" style={{ minWidth: '2100px' }}>
                          <thead className="bg-muted/50 sticky top-0">
                            <tr>
                              <th className="text-center p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '150px' }}>
                                <div className="flex items-center gap-1">
                                  Customer
                                  {editMode && childInvoices.length > 1 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); pasteAllField('customer_name'); }}
                                            className="p-0.5 hover:bg-muted rounded"
                                            data-testid="paste-all-customer"
                                          >
                                            <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '180px' }}>
                                <div className="flex items-center gap-1">
                                  Address
                                  {editMode && childInvoices.length > 1 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); pasteAllField('unit_address'); }}
                                            className="p-0.5 hover:bg-muted rounded"
                                            data-testid="paste-all-address"
                                          >
                                            <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '100px' }}>Account No</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '85px' }}>Commodity</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '75px' }}>Type</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '110px' }}>Rate Schedule</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '100px' }}>Invoice No</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '95px' }}>
                                <div className="flex items-center gap-1">
                                  Due Date
                                  {editMode && childInvoices.length > 1 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); pasteAllField('due_date'); }}
                                            className="p-0.5 hover:bg-muted rounded"
                                            data-testid="paste-all-due-date"
                                          >
                                            <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '95px' }}>
                                <div className="flex items-center gap-1">
                                  Period From
                                  {editMode && childInvoices.length > 1 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); pasteAllField('period_from'); }}
                                            className="p-0.5 hover:bg-muted rounded"
                                            data-testid="paste-all-period-from"
                                          >
                                            <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '95px' }}>
                                <div className="flex items-center gap-1">
                                  Period To
                                  {editMode && childInvoices.length > 1 && (
                                    <TooltipProvider>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); pasteAllField('period_to'); }}
                                            className="p-0.5 hover:bg-muted rounded"
                                            data-testid="paste-all-period-to"
                                          >
                                            <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                          </button>
                                        </TooltipTrigger>
                                        <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  )}
                                </div>
                              </th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '85px' }}>Bal B/F</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '80px' }}>Sales Tax</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '90px' }}>New Charges</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '90px' }}>Total Due</th>
                              <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '80px' }}>Status</th>
                              {editMode && <th className="p-2 w-8 border-b"></th>}
                            </tr>
                          </thead>
                          <tbody>
                            {childInvoices.map((invoice, index) => (
                              <tr 
                                key={index} 
                                className={`border-b border-border hover:bg-muted/25 ${invoice.page_number ? 'cursor-pointer' : ''} ${editingRowId === `invoices-${index}` ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}
                                onClick={() => !editMode && invoice.page_number && navigateToPage(invoice.page_number, invoice.child_account_no || invoice.unit_address)}
                                onFocus={() => {
                                  if (editMode) {
                                    setEditingRowId(`invoices-${index}`);
                                    if (invoice.page_number) navigateToPage(invoice.page_number, invoice.child_account_no || invoice.unit_address);
                                  }
                                }}
                                title={invoice.page_number ? `Click to go to page ${invoice.page_number}` : ''}
                              >
                                <td className="p-2 text-center">
                                  {invoice.page_number ? (
                                    <Badge variant="secondary" className="rounded-none text-[10px] px-1.5 py-0.5">
                                      p.{invoice.page_number}
                                    </Badge>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">-</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={invoice.customer_name || ''}
                                      onChange={(e) => updateChildInvoice(index, 'customer_name', e.target.value)}
                                      className="rounded-none h-8 text-xs min-w-[130px]"
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{invoice.customer_name || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={invoice.unit_address || ''}
                                      onChange={(e) => updateChildInvoice(index, 'unit_address', e.target.value)}
                                      className="rounded-none h-8 text-xs min-w-[160px]"
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{invoice.unit_address || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2 font-mono">
                                  {editMode ? (
                                    <Input
                                      value={invoice.child_account_no || ''}
                                      onChange={(e) => updateChildInvoice(index, 'child_account_no', e.target.value)}
                                      className="rounded-none h-8 text-xs min-w-[90px]"
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{invoice.child_account_no || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Select
                                      value={invoice.commodity || 'Electricity'}
                                      onValueChange={(value) => updateChildInvoice(index, 'commodity', value)}
                                    >
                                      <SelectTrigger className="rounded-none h-8 w-24 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Electricity">Electricity</SelectItem>
                                        <SelectItem value="Gas">Gas</SelectItem>
                                        <SelectItem value="Water">Water</SelectItem>
                                        <SelectItem value="Sewer">Sewer</SelectItem>
                                        <SelectItem value="Waste">Waste</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Badge variant="outline" className="rounded-none text-xs">
                                      {invoice.commodity || '-'}
                                    </Badge>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Select
                                      value={invoice.service_type || 'Supply'}
                                      onValueChange={(value) => updateChildInvoice(index, 'service_type', value)}
                                    >
                                      <SelectTrigger className="rounded-none h-8 w-20 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Supply">Supply</SelectItem>
                                        <SelectItem value="Delivery">Delivery</SelectItem>
                                        <SelectItem value="Both">Both</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <span className="text-xs">{invoice.service_type || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={invoice.rate_schedule || ''}
                                      onChange={(e) => updateChildInvoice(index, 'rate_schedule', e.target.value)}
                                      className="rounded-none h-8 text-xs min-w-[90px]"
                                    />
                                  ) : (
                                    <span className="text-xs">{invoice.rate_schedule || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2 font-mono">
                                  {editMode ? (
                                    <Input
                                      value={invoice.child_invoice_no || ''}
                                      onChange={(e) => updateChildInvoice(index, 'child_invoice_no', e.target.value)}
                                      className="rounded-none h-8 text-xs min-w-[90px]"
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{invoice.child_invoice_no || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      type="date"
                                      value={invoice.due_date || ''}
                                      onChange={(e) => updateChildInvoice(index, 'due_date', e.target.value)}
                                      className="rounded-none h-8 text-xs w-[110px]"
                                    />
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <span className="whitespace-nowrap text-xs">{invoice.due_date || '-'}</span>
                                      {invoiceDateValidations[index]?.due_date && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span>
                                                {invoiceDateValidations[index].due_date.isValid ? (
                                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                                ) : (
                                                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                                                )}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p className="text-xs">{invoiceDateValidations[index].due_date.isValid ? 'Valid' : invoiceDateValidations[index].due_date.message}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      type="date"
                                      value={invoice.period_from || ''}
                                      onChange={(e) => updateChildInvoice(index, 'period_from', e.target.value)}
                                      className="rounded-none h-8 text-xs w-[110px]"
                                    />
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <span className="whitespace-nowrap text-xs">{invoice.period_from || '-'}</span>
                                      {invoiceDateValidations[index]?.period_from && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span>
                                                {invoiceDateValidations[index].period_from.isValid ? (
                                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                                ) : (
                                                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                                                )}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p className="text-xs">{invoiceDateValidations[index].period_from.isValid ? 'Valid' : invoiceDateValidations[index].period_from.message}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      type="date"
                                      value={invoice.period_to || ''}
                                      onChange={(e) => updateChildInvoice(index, 'period_to', e.target.value)}
                                      className="rounded-none h-8 text-xs w-[110px]"
                                    />
                                  ) : (
                                    <div className="flex items-center gap-1">
                                      <span className="whitespace-nowrap text-xs">{invoice.period_to || '-'}</span>
                                      {invoiceDateValidations[index]?.period_to && invoiceDateValidations[index]?.period_range && (
                                        <TooltipProvider>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span>
                                                {invoiceDateValidations[index].period_to.isValid && invoiceDateValidations[index].period_range.isValid ? (
                                                  <CheckCircle className="h-3 w-3 text-green-500" />
                                                ) : (
                                                  <AlertTriangle className="h-3 w-3 text-amber-500" />
                                                )}
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                              <p className="text-xs">
                                                {invoiceDateValidations[index].period_to.isValid && invoiceDateValidations[index].period_range.isValid 
                                                  ? 'Valid' 
                                                  : invoiceDateValidations[index].period_to.message || invoiceDateValidations[index].period_range.message}
                                              </p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={invoice.balance_brought_forward || '0'}
                                      onChange={(e) => updateChildInvoice(index, 'balance_brought_forward', e.target.value)}
                                      className="rounded-none h-8 text-xs w-16"
                                    />
                                  ) : (
                                    <span className="text-xs">${invoice.balance_brought_forward || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={invoice.sales_tax || '0'}
                                      onChange={(e) => updateChildInvoice(index, 'sales_tax', e.target.value)}
                                      className="rounded-none h-8 text-xs w-16"
                                    />
                                  ) : (
                                    <span className="text-xs">${invoice.sales_tax || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={invoice.new_charges || '0'}
                                      onChange={(e) => updateChildInvoice(index, 'new_charges', e.target.value)}
                                      className="rounded-none h-8 text-xs w-16"
                                    />
                                  ) : (
                                    <span className="text-xs">${invoice.new_charges || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2 font-medium">
                                  {editMode ? (
                                    <Input
                                      value={invoice.total_amount_due || '0'}
                                      onChange={(e) => updateChildInvoice(index, 'total_amount_due', e.target.value)}
                                      className="rounded-none h-8 text-xs w-16"
                                    />
                                  ) : (
                                    <span className="text-xs">${invoice.total_amount_due || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Select
                                      value={invoice.review_status || 'review'}
                                      onValueChange={(value) => updateChildInvoice(index, 'review_status', value)}
                                    >
                                      <SelectTrigger className="rounded-none h-8 w-20 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="review">Review</SelectItem>
                                        <SelectItem value="approved">Approved</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Badge 
                                      variant={invoice.review_status === 'approved' ? 'default' : 'outline'}
                                      className={`rounded-none text-xs ${invoice.review_status === 'approved' ? 'bg-blue-600' : ''}`}
                                    >
                                      {invoice.review_status || 'Review'}
                                    </Badge>
                                  )}
                                </td>
                                {editMode && (
                                  <td className="p-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeChildInvoice(index)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </TabsContent>
                
                {/* Meter Tab */}
                <TabsContent value="meter" className="m-0 h-full overflow-hidden">
                  <div className="h-full flex flex-col p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Meter Readings</h3>
                      </div>
                      {editMode && (
                        <Button size="sm" onClick={addMeterRead} className="rounded-none">
                          <Plus className="h-4 w-4 mr-1" /> Add Meter
                        </Button>
                      )}
                    </div>
                    
                    {meterReads.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No meter readings found
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 overflow-auto border border-border">
                          <table className="w-full text-sm min-w-[1200px]">
                            <thead className="bg-muted/50 sticky top-0">
                              <tr>
                                <th className="text-center p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Account No</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Meter No</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Commodity</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Multiplier</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">
                                  <div className="flex items-center gap-1">
                                    Prev Date
                                    {editMode && meterReads.length > 1 && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); pasteAllMeterField('previous_read_date'); }}
                                              className="p-0.5 hover:bg-muted rounded"
                                              data-testid="paste-all-prev-date"
                                            >
                                              <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                            </button>
                                          </TooltipTrigger>
                                          <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </div>
                                </th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">
                                  <div className="flex items-center gap-1">
                                    Curr Date
                                    {editMode && meterReads.length > 1 && (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <button
                                              onClick={(e) => { e.stopPropagation(); pasteAllMeterField('current_read_date'); }}
                                              className="p-0.5 hover:bg-muted rounded"
                                              data-testid="paste-all-curr-date"
                                            >
                                              <Copy className="h-3 w-3 text-muted-foreground hover:text-primary" />
                                            </button>
                                          </TooltipTrigger>
                                          <TooltipContent><p className="text-xs">Paste to all rows</p></TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    )}
                                  </div>
                                </th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Prev Read</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Curr Read</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Type</th>
                                {editMode && <th className="p-2 text-center text-xs font-medium border-b" style={{ minWidth: '70px' }}>Actions</th>}
                              </tr>
                            </thead>
                            <tbody>
                              {meterReads.map((meter, index) => {
                                const rowId = `meters-${index}`;
                                const isRowActive = editingRowId === rowId;
                                const isRowReviewed = reviewedRows.has(rowId);
                                return (
                                  <tr 
                                    key={index} 
                                    className={`border-b border-border transition-colors ${isRowActive ? 'bg-blue-50 ring-2 ring-blue-400 ring-inset' : ''} ${isRowReviewed ? 'bg-green-50' : ''} ${!editMode && meter.page_number ? 'cursor-pointer hover:bg-muted/25' : ''}`}
                                    onClick={() => !editMode && meter.page_number && navigateToPage(meter.page_number, meter.meter_number || meter.child_account_no)}
                                    title={meter.page_number ? `Click to go to page ${meter.page_number}` : ''}
                                  >
                                    <td className="p-2 text-center">
                                      {meter.page_number ? (
                                        <Badge 
                                          variant="secondary" 
                                          className={`rounded-none text-[10px] px-1.5 py-0.5 cursor-pointer hover:bg-muted ${isRowActive ? 'bg-blue-100' : ''}`}
                                          onClick={(e) => { e.stopPropagation(); navigateToPage(meter.page_number, meter.meter_number || meter.child_account_no); }}
                                        >
                                          p.{meter.page_number}
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">-</span>
                                      )}
                                    </td>
                                    <td className="p-2 font-mono">
                                      {editMode ? (
                                        <Input
                                          value={meter.child_account_no || ''}
                                          onChange={(e) => updateMeterRead(index, 'child_account_no', e.target.value)}
                                          onFocus={() => handleRowFocus(rowId, meter.page_number)}
                                          className="rounded-none h-8 text-xs w-24"
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.child_account_no || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2 font-mono">
                                      {editMode ? (
                                        <Input
                                          value={meter.meter_number || ''}
                                          onChange={(e) => updateMeterRead(index, 'meter_number', e.target.value)}
                                          onFocus={() => handleRowFocus(rowId, meter.page_number)}
                                          className="rounded-none h-8 text-xs w-24"
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.meter_number || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {editMode ? (
                                        <Select
                                          value={meter.commodity || 'Electricity'}
                                          onValueChange={(v) => updateMeterRead(index, 'commodity', v)}
                                          onOpenChange={(open) => open && handleRowFocus(rowId, meter.page_number)}
                                        >
                                          <SelectTrigger className="rounded-none h-8 text-xs w-24">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="Electricity">Electricity</SelectItem>
                                            <SelectItem value="Gas">Gas</SelectItem>
                                            <SelectItem value="Water">Water</SelectItem>
                                            <SelectItem value="Sewer">Sewer</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        <span className="text-xs">{meter.commodity || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {editMode ? (
                                        <Input
                                          value={meter.multiplier || 'N/A'}
                                          onChange={(e) => updateMeterRead(index, 'multiplier', e.target.value)}
                                          className="rounded-none h-8 text-xs w-16"
                                          placeholder="N/A"
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.multiplier || 'N/A'}</span>
                                      )}
                                    </td>
                                    <td className={`p-2 ${hasDateError ? 'bg-yellow-100' : ''}`}>
                                      {editMode ? (
                                        <Input
                                          type="date"
                                          value={meter.previous_read_date || ''}
                                          onChange={(e) => updateMeterRead(index, 'previous_read_date', e.target.value)}
                                          className="rounded-none h-8 text-xs w-28"
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.previous_read_date || '-'}</span>
                                      )}
                                    </td>
                                    <td className={`p-2 ${hasDateError ? 'bg-yellow-100' : ''}`}>
                                      {editMode ? (
                                        <Input
                                          type="date"
                                          value={meter.current_read_date || ''}
                                          onChange={(e) => updateMeterRead(index, 'current_read_date', e.target.value)}
                                          className="rounded-none h-8 text-xs w-28"
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.current_read_date || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {editMode ? (
                                        <Input
                                          value={meter.previous_read || ''}
                                          onChange={(e) => updateMeterRead(index, 'previous_read', e.target.value)}
                                          className="rounded-none h-8 text-xs w-20"
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.previous_read || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {editMode ? (
                                        <Input
                                          value={meter.current_read || ''}
                                          onChange={(e) => updateMeterRead(index, 'current_read', e.target.value)}
                                          className="rounded-none h-8 text-xs w-20"
                                        />
                                      ) : (
                                        <span className="text-xs">{meter.current_read || '-'}</span>
                                      )}
                                    </td>
                                    <td className="p-2">
                                      {editMode ? (
                                        <Select
                                          value={meter.read_type || 'Actual'}
                                          onValueChange={(value) => updateMeterRead(index, 'read_type', value)}
                                          onOpenChange={(open) => open && handleRowFocus(rowId, meter.page_number)}
                                        >
                                          <SelectTrigger className="rounded-none h-8 w-20 text-xs">
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="Actual">Actual</SelectItem>
                                            <SelectItem value="Estimated">Estimated</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      ) : (
                                        <span className="text-xs">{meter.read_type || '-'}</span>
                                      )}
                                    </td>
                                    {editMode && (
                                      <td className="p-2 text-center">
                                        <div className="flex items-center justify-center gap-1">
                                          <Button
                                            size="sm"
                                            variant={isRowReviewed ? "default" : "outline"}
                                            className={`h-6 w-6 p-0 rounded-none ${isRowReviewed ? 'bg-green-600 hover:bg-green-700' : ''}`}
                                            onClick={() => toggleRowReviewed(rowId)}
                                            title={isRowReviewed ? 'Marked as reviewed' : 'Mark as reviewed'}
                                          >
                                            <Check className="h-3 w-3" />
                                          </Button>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeMeterRead(index)}
                                            className="h-6 w-6 p-0"
                                            title="Delete row"
                                          >
                                            <Trash2 className="h-3 w-3 text-red-500" />
                                          </Button>
                                        </div>
                                      </td>
                                    )}
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                </TabsContent>
                
                {/* Usage Tab */}
                <TabsContent value="usage" className="m-0 h-full overflow-hidden">
                  <div className="h-full flex flex-col p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">Usage Details</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                          Total usage with original UOM, rate and charge • {filteredUsageDetails.length} items
                        </p>
                      </div>
                      {editMode && (
                        <Button size="sm" onClick={addUsageDetail} className="rounded-none">
                          <Plus className="h-4 w-4 mr-1" /> Add Usage
                        </Button>
                      )}
                    </div>
                    
                    {filteredUsageDetails.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No usage details found
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 overflow-auto border border-border">
                          <table className="w-full text-sm min-w-[1250px]">
                            <thead className="bg-muted/50 sticky top-0">
                              <tr>
                                <th className="text-center p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '50px' }}>Page</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Account No</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Meter No</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Commodity</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Type</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap" style={{ minWidth: '160px' }}>Description</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Usage</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">UOM</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Rate</th>
                                <th className="text-left p-2 text-xs font-medium border-b whitespace-nowrap">Charge</th>
                                {editMode && <th className="p-2 w-8 border-b"></th>}
                              </tr>
                            </thead>
                            <tbody>
                              {filteredUsageDetails.map((usage, index) => {
                                return (
                                  <tr 
                                    key={index} 
                                    className={`border-b border-border hover:bg-muted/25 ${usage.page_number ? 'cursor-pointer' : ''} ${editingRowId === `usage-${index}` ? 'bg-blue-50 ring-2 ring-blue-300 ring-inset' : ''}`}
                                    onClick={() => !editMode && usage.page_number && navigateToPage(usage.page_number, usage.line_item_description || usage.meter_number)}
                                    onFocus={() => {
                                      if (editMode) {
                                        setEditingRowId(`usage-${index}`);
                                        if (usage.page_number) navigateToPage(usage.page_number, usage.line_item_description || usage.meter_number);
                                      }
                                    }}
                                    title={usage.page_number ? `Click to go to page ${usage.page_number}` : ''}
                                  >
                                    <td className="p-2 text-center">
                                      {usage.page_number ? (
                                        <Badge variant="secondary" className="rounded-none text-[10px] px-1.5 py-0.5">
                                          p.{usage.page_number}
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">-</span>
                                      )}
                                </td>
                                <td className="p-2 font-mono">
                                  {editMode ? (
                                    <Input
                                      value={usage.child_account_no || ''}
                                      onChange={(e) => updateUsageDetail(index, 'child_account_no', e.target.value)}
                                      className="rounded-none h-8 text-xs w-24"
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{usage.child_account_no || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2 font-mono">
                                  {editMode ? (
                                    <Input
                                      value={usage.meter_number || ''}
                                      onChange={(e) => updateUsageDetail(index, 'meter_number', e.target.value)}
                                      className="rounded-none h-8 text-xs w-24"
                                    />
                                  ) : (
                                    <span className="whitespace-nowrap text-xs">{usage.meter_number || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Select
                                      value={usage.commodity || 'Electricity'}
                                      onValueChange={(value) => updateUsageDetail(index, 'commodity', value)}
                                    >
                                      <SelectTrigger className="rounded-none h-8 w-24 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Electricity">Electricity</SelectItem>
                                        <SelectItem value="Gas">Gas</SelectItem>
                                        <SelectItem value="Water">Water</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <Badge variant="outline" className="rounded-none text-xs">
                                      {usage.commodity || '-'}
                                    </Badge>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Select
                                      value={usage.usage_type || 'Supply'}
                                      onValueChange={(value) => updateUsageDetail(index, 'usage_type', value)}
                                    >
                                      <SelectTrigger className="rounded-none h-8 w-20 text-xs">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Supply">Supply</SelectItem>
                                        <SelectItem value="Delivery">Delivery</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  ) : (
                                    <span className="text-xs">{usage.usage_type || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={usage.line_item_description || ''}
                                      onChange={(e) => updateUsageDetail(index, 'line_item_description', e.target.value)}
                                      className="rounded-none h-8 text-xs"
                                      style={{ minWidth: '140px' }}
                                    />
                                  ) : (
                                    <span className="text-xs">{usage.line_item_description || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={usage.usage_quantity || '0'}
                                      onChange={(e) => updateUsageDetail(index, 'usage_quantity', e.target.value)}
                                      className="rounded-none h-8 text-xs w-20"
                                    />
                                  ) : (
                                    <span className="text-xs font-semibold">{usage.usage_quantity || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={usage.uom || ''}
                                      onChange={(e) => updateUsageDetail(index, 'uom', e.target.value)}
                                      className="rounded-none h-8 text-xs w-16"
                                    />
                                  ) : (
                                    <span className="text-xs">{usage.uom || '-'}</span>
                                  )}
                                </td>
                                <td className="p-2">
                                  {editMode ? (
                                    <Input
                                      value={usage.rate || '0'}
                                      onChange={(e) => updateUsageDetail(index, 'rate', e.target.value)}
                                      className="rounded-none h-8 text-xs w-16"
                                    />
                                  ) : (
                                    <span className="text-xs">{usage.rate || '0'}</span>
                                  )}
                                </td>
                                <td className="p-2 font-medium">
                                  {editMode ? (
                                    <Input
                                      value={usage.line_item_charge || '0'}
                                      onChange={(e) => updateUsageDetail(index, 'line_item_charge', e.target.value)}
                                      className="rounded-none h-8 text-xs w-20"
                                    />
                                  ) : (
                                    <span className="text-xs">${usage.line_item_charge || '0'}</span>
                                  )}
                                </td>
                                {editMode && (
                                  <td className="p-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => removeUsageDetail(index)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </td>
                                )}
                              </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
      
      {/* Audit Log Modal */}
      <Dialog open={showAuditModal} onOpenChange={setShowAuditModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Document History
            </DialogTitle>
            <DialogDescription>
              View all changes made to this document
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            {auditLogs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No history available</p>
            ) : (
              auditLogs.map((log, index) => (
                <div key={index} className="border border-border p-3 bg-muted/30">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium">{log.action}</p>
                      <p className="text-xs text-muted-foreground mt-1">{log.details}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-muted-foreground">{log.user_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {log.created_at ? format(new Date(log.created_at), 'MMM d, yyyy h:mm a') : ''}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsolidatedViewer;
